import { usePollInterval } from "@src/lib/hooks/usePollInterval";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import gql from "graphql-tag";
import { v4 as uuidv4 } from "uuid";
import {
  AgentActivityEntry,
  AgentActivityScope,
  agentScopeForContent,
  useAgentActivityStore,
} from "@src/features/libraryItem/agentActivity/agentActivityStore";
import { describeBuilderAction } from "@src/features/libraryItem/agentActivity/describeBuilderAction";
import { targetScreenIdFromAction } from "@src/features/libraryItem/agentActivity/targetScreenFromAction";
import { ClientIdContext } from "@src/features/builders/shared/presence/ClientIdContext";
import {
  BuilderActionFragment,
  useBuilderActionsQuery,
} from "@src/features/libraryItem/hooks/usePollServerForBuilderActions.generated";
import { isNullOrUndefined } from "@src/lib/utils/typeguards";
import {
  BuilderActionInput,
  GetBuilderActionsInput,
} from "@src/types.generated";
import {
  BuilderActionContentIds,
  BuilderActionContextContent,
  BuilderActionContextProviderProps,
} from "@src/features/libraryItem/contexts/BuilderActionContext";

type Args = {
  contentToPollActionsFor: BuilderActionContextContent[];
  applyActionFromServerFn: BuilderActionContextProviderProps["applyActionFromServer"];
};

type BuilderActionFragmentWithTypedPayload = BuilderActionFragment & {
  payload: BuilderActionInput;
  timestamp: Date;
};

const usePollServerForBuilderActions = (args: Args): void => {
  const [contentIdToLastAppliedActionId, setContentIdToLastAppliedActionId] =
    useState<Map<string, number | null | undefined>>(() => {
      const map = new Map<string, number | null | undefined>();
      for (const content of args.contentToPollActionsFor) {
        map.set(
          getContentId(content.contentIds),
          content.initialLastAppliedBuilderActionId,
        );
      }
      return map;
    });
  useEffect(() => {
    for (const content of args.contentToPollActionsFor) {
      const lastAppliedIdInState = contentIdToLastAppliedActionId.get(
        getContentId(content.contentIds),
      );
      if (
        isNullOrUndefined(lastAppliedIdInState) &&
        content.initialLastAppliedBuilderActionId
      ) {
        setContentIdToLastAppliedActionId((prevState) => {
          const newState = new Map(prevState);
          newState.set(
            getContentId(content.contentIds),
            content.initialLastAppliedBuilderActionId,
          );
          return newState;
        });
      }
    }
  }, [args.contentToPollActionsFor, contentIdToLastAppliedActionId]);
  const clientIdContext = useContext(ClientIdContext);
  const recordAgentActivity = useAgentActivityStore((s) => s.recordActivity);
  const clearAgentActivity = useAgentActivityStore((s) => s.clearActivity);
  const setWorkingScreen = useAgentActivityStore((s) => s.setWorkingScreen);
  const appliedActionIdsRef = useRef<Set<number>>(new Set());
  const rootContentIds = args.contentToPollActionsFor[0]?.contentIds;
  const rootContentKey = rootContentIds
    ? [
        rootContentIds.courseId,
        rootContentIds.moduleId,
        rootContentIds.skillId,
        rootContentIds.trainingResourceId,
      ].join("-")
    : "";
  useEffect(() => {
    appliedActionIdsRef.current = new Set();
    clearAgentActivity();
    return () => clearAgentActivity();
  }, [rootContentKey, clearAgentActivity]);
  const pollInterval = usePollInterval(1000, 1000 * 60);
  const pollInputs: GetBuilderActionsInput[] = useMemo(() => {
    return args.contentToPollActionsFor.map((x) => ({
      ...x.contentIds,
      afterBuilderActionId: contentIdToLastAppliedActionId.get(
        getContentId(x.contentIds),
      ),
      clientId: clientIdContext?.clientId || uuidv4(),
    }));
  }, [
    args.contentToPollActionsFor,
    clientIdContext?.clientId,
    contentIdToLastAppliedActionId,
  ]);
  useBuilderActionsQuery({
    pollInterval,
    skip: !clientIdContext?.clientId || pollInputs.length === 0,
    variables: {
      inputs: pollInputs,
    },
    onCompleted(data) {
      const actions =
        data.BuilderActions as BuilderActionFragmentWithTypedPayload[];
      const observedAt = Date.now();
      // Group activity per content scope: the module builder polls actions
      // for the module AND every child course/skill at once, so recording
      // them into one shared bucket made every embedded course's indicator
      // show whichever sibling acted last.
      const entriesByScope = new Map<
        AgentActivityScope,
        AgentActivityEntry[]
      >();
      for (const action of actions) {
        setContentIdToLastAppliedActionId((prevState) => {
          const newState = new Map(prevState);
          newState.set(
            getContentId({
              courseId: action.payload.courseBuilderAction?.courseId,
              moduleId: action.payload.moduleBuilderAction?.moduleId,
            }),
            action.id,
          );
          return newState;
        });
        if (appliedActionIdsRef.current.has(action.id)) {
          continue;
        }
        appliedActionIdsRef.current.add(action.id);
        // This line of code is problematic. the action here doesn't actually have a timestamp and this cast is invalid.
        // what do we do here?
        args.applyActionFromServerFn(
          action as BuilderActionFragmentWithTypedPayload,
        );
        const scope = agentScopeForContent({
          courseId: action.payload.courseBuilderAction?.courseId,
          skillId: action.payload.skillBuilderAction?.skillId,
          moduleId: action.payload.moduleBuilderAction?.moduleId,
          trainingResourceId:
            action.payload.trainingResourceBuilderAction?.trainingResourceId,
        });
        if (scope) {
          const entries = entriesByScope.get(scope) ?? [];
          entries.push({
            id: action.id,
            label: describeBuilderAction(action.payload),
            observedAt,
          });
          entriesByScope.set(scope, entries);
        }
        const screenId = targetScreenIdFromAction(action.payload);
        if (screenId) {
          setWorkingScreen(screenId, observedAt);
        }
      }
      for (const [scope, entries] of entriesByScope) {
        recordAgentActivity(scope, entries);
      }
    },
  });
};

export default usePollServerForBuilderActions;

gql`
  query BuilderActions($inputs: [GetBuilderActionsInput!]!) {
    BuilderActions(inputs: $inputs) {
      ...BuilderAction
    }
  }
  fragment BuilderAction on BuilderAction {
    id
    uuid
    clientId
    payload
    addedModuleContentMembership {
      ...ModuleBuilderContentMembership
    }
  }
`;

const getContentId = (
  contentIds: Pick<BuilderActionContentIds, "courseId" | "moduleId">,
): string => {
  return `${contentIds.courseId}-${contentIds.moduleId}`;
};
